import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ICategory } from '../types/damTypes';
import { API_BASE_URL } from '../config';
import { 
  Modal, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  CircularProgress, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';

interface EditCategoryModalProps {
  category: ICategory | null; // The category to edit (null when modal is closed)
  onClose: () => void; // Callback to close the modal
  onCategoryEdited: () => void; // Callback to refresh list after editing
  allCategories: ICategory[]; // All available categories for parent selection
}

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

const EditCategoryModal: React.FC<EditCategoryModalProps> = ({ category, onClose, onCategoryEdited, allCategories }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentCategory, setParentCategory] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Get eligible parent categories (exclude self and any subcategories of this category)
  const getEligibleParents = () => {
    if (!category) return [];

    // A category cannot be its own parent
    // A category cannot have one of its descendants as its parent (to avoid circular references)
    const descendantIds = findAllDescendants(category._id);
    
    return allCategories.filter(cat => 
      cat._id !== category._id && !descendantIds.includes(cat._id)
    );
  };

  // Find all descendants of a category
  const findAllDescendants = (categoryId: string): string[] => {
    const directDescendants = allCategories.filter(cat => {
      if (typeof cat.parentCategory === 'string') {
        return cat.parentCategory === categoryId;
      } else if (cat.parentCategory && typeof cat.parentCategory === 'object') {
        return (cat.parentCategory as {_id: string})._id === categoryId;
      }
      return false;
    });
    
    let allDescendants: string[] = directDescendants.map(cat => cat._id);
    
    // Recursively find descendants of descendants
    directDescendants.forEach(descendant => {
      allDescendants = [...allDescendants, ...findAllDescendants(descendant._id)];
    });
    
    return allDescendants;
  };

  // Filter main categories (excluding the current category and its subcategories)
  const eligibleParents = getEligibleParents();

  // Update form state when the category prop changes
  useEffect(() => {
    if (category) {
      setName(category.name);
      setDescription(category.description || '');
      
      // Handle parentCategory which could be a string ID or an object with _id
      if (category.parentCategory) {
        if (typeof category.parentCategory === 'string') {
          setParentCategory(category.parentCategory);
        } else if (typeof category.parentCategory === 'object' && category.parentCategory) {
          setParentCategory((category.parentCategory as {_id: string})._id);
        }
      } else {
        setParentCategory(null);
      }
      
      setMessage(null); // Clear message when a new category is loaded
    } else {
      // Clear form if no category is selected (modal is closing)
      setName('');
      setDescription('');
      setParentCategory(null);
      setMessage(null);
    }
  }, [category]);

  // If no category is provided, don't render
  if (!category) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!category) return; // Should not happen as per the above check

    setLoading(true);
    setMessage(null);

    try {
      const response = await axios.put(`${API_BASE_URL}/api/categories/${category._id}`, {
        name,
        description,
        parentCategory: parentCategory || null,
      });
      setMessage(`Kategori "${response.data.name}" uppdaterad.`);
      if (onCategoryEdited) {
        onCategoryEdited(); // Notify parent to refresh the list
      }
    } catch (err: any) {
      console.error('Error updating category:', err);
      setMessage(`Kunde inte uppdatera kategorin "${category.name}": ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={!!category}
      onClose={onClose}
      aria-labelledby="edit-category-modal-title"
    >
      <Box sx={style}>
        <Typography id="edit-category-modal-title" variant="h6" component="h2" gutterBottom>
          Redigera kategori: {category.name}
        </Typography>
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Namn"
            variant="outlined"
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            disabled={loading}
          />
          
          <TextField
            label="Beskrivning"
            variant="outlined"
            size="small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            fullWidth
            disabled={loading}
          />
          
          <FormControl fullWidth size="small">
            <InputLabel id="edit-parent-category-label">Huvudkategori</InputLabel>
            <Select
              labelId="edit-parent-category-label"
              id="edit-parent-category"
              value={parentCategory || ''}
              label="Huvudkategori"
              onChange={(e) => setParentCategory(e.target.value === '' ? null : e.target.value)}
              disabled={loading}
            >
              <MenuItem value=""><em>Ingen (Huvudkategori)</em></MenuItem>
              {eligibleParents.map((cat) => (
                <MenuItem key={cat._id} value={cat._id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Välj en huvudkategori eller lämna tomt för att göra detta till en huvudkategori
            </FormHelperText>
          </FormControl>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button 
              variant="outlined" 
              onClick={onClose} 
              disabled={loading}
            >
              Avbryt
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading || !name}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? 'Sparar...' : 'Spara ändringar'}
            </Button>
          </Box>
        </Box>
        
        {message && (
          <Typography 
            variant="body2" 
            color={message.includes('Kunde inte') ? 'error' : 'success'} 
            sx={{ mt: 2 }}
          >
            {message}
          </Typography>
        )}
      </Box>
    </Modal>
  );
};

export default EditCategoryModal; 